/**
 * @file SenderSelect.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-09
 * @modified 2026-09-16
 *
 * "Send as" picker for outbound email. Defaults to the acting user's own
 * @excel-agri.com address and offers the four shared mailboxes. Emits a `sendAs`
 * token ('me' | 'accounts' | 'admin' | 'feed' | 'transport') that the server
 * resolves via emailService.resolveFrom — the client never sends a raw From, so
 * the sender is always constrained to the allowlist.
 */

import React from 'react';
import Dropdown from './Dropdown';

const SHARED_SENDERS = [
  { value: 'accounts', label: 'accounts@excel-agri.com' },
  { value: 'admin', label: 'admin@excel-agri.com' },
  { value: 'feed', label: 'feed@excel-agri.com' },
  { value: 'transport', label: 'transport@excel-agri.com' },
];

const isOwnAddress = (user) => !!user?.email && /@excel-agri\.com$/i.test(user.email);

// Default token — always the acting user's own address. The server resolves
// 'me' to req.user's address (falling back to accounts@ if they somehow have no
// @excel-agri address), so this is safe even when the client doesn't know the
// user's email. Callers that want a shared mailbox as the default (e.g. customer
// statements → accounts@) pass their own initial value instead.
export function defaultSender() {
  return 'me';
}

function SenderSelect({ user, value, onChange, disabled, only = null }) {
  // The 'me' option is always offered; the label is nicer when we know the
  // user's address, but it works either way (resolved server-side).
  const meLabel = isOwnAddress(user) ? `${user.email} (you)` : 'My address (you)';
  // `only` narrows the list to the senders that make sense for a document —
  // invoices and purchase orders go from you, accounts@ or admin@, never
  // transport@.
  const options = [{ value: 'me', label: meLabel }, ...SHARED_SENDERS]
    .filter((o) => !Array.isArray(only) || only.includes(o.value));

  return (
    <Dropdown
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder="Send from…"
    />
  );
}

export default SenderSelect;
